import { useEffect, useState } from 'react';
import { dndStatus, requestDndAccess } from '../lib/push.js';
import Button from './Button.jsx';
import Card from './Card.jsx';

const DISMISSED = 'hfb-dnd-prompt-dismissed';

/**
 * Asks for permission to ring through Do Not Disturb.
 *
 * The alert already sounds on the alarm stream, so a silenced ringer doesn't
 * mute it. Do Not Disturb is stricter: Android only lets an app past it once
 * the person has granted "Do Not Disturb access" in system settings, which no
 * app can grant itself. So this explains why, opens that screen, and then gets
 * out of the way — it only appears where the alert matters (the driver and
 * admin apps) and only while the access is missing.
 */
export default function DndPrompt() {
  const [state, setState] = useState(null); // null = unknown/not applicable
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let live = true;
    const check = () => dndStatus().then((s) => live && setState(s)).catch(() => {});
    check();
    // They grant it in system settings and come back, so re-check on return.
    const onVisible = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!state || state.dndAccess || hidden) return null;

  return (
    <Card className="mb-4" tone="raised">
      <p className="font-display text-lg text-heading">Let orders ring through Do Not Disturb</p>
      <p className="mt-1 text-sm text-ink-muted">
        New orders already ring with the phone on silent. To ring while Do Not Disturb is on, Android
        needs your permission. It is one switch, on the next screen.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => requestDndAccess()}>Open the setting</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setHidden(true);
            try {
              localStorage.setItem(DISMISSED, '1');
            } catch {
              /* storage off — it stays hidden for this session */
            }
          }}
        >
          Not now
        </Button>
      </div>
    </Card>
  );
}
